import { useEffect, useState, type FormEvent } from "react";
import { Copy, KeyRound, Plus, Webhook } from "lucide-react";
import {
  createResource,
  loadIntegrationSettings,
  loadOperatorPermissions,
  loadOperators,
  type IntegrationSettingsData,
} from "./api.js";
import "./integration-settings.css";
interface Session {
  tenantId: string;
  token: string;
  csrf: string;
}
const value = (row: Record<string, unknown>, key: string) =>
  String(row[key] ?? "");
export function IntegrationSettings({ session }: { session: Session }) {
  const [data, setData] = useState<IntegrationSettingsData>(),
    [operators, setOperators] = useState<Record<string, unknown>[]>([]),
    [permissions, setPermissions] = useState<Record<string, unknown>[]>([]),
    [secret, setSecret] = useState<{ title: string; value: string }>(),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(false);
  async function load() {
    setError("");
    try {
      const [result, users, scopes] = await Promise.all([
        loadIntegrationSettings(session.tenantId, session.token),
        loadOperators(session.tenantId, session.token),
        loadOperatorPermissions(session.tenantId, session.token),
      ]);
      setData(result);
      setOperators(users);
      setPermissions(scopes);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Unable to load API and webhook settings",
      );
    }
  }
  useEffect(() => {
    void load();
  }, [session.tenantId, session.token]);
  async function createCredential(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    const form = event.currentTarget,
      fields = new FormData(form);
    try {
      const result = await createResource<{
        id: string;
        token: string;
        prefix: string;
      }>(
        "/api/v1/integrations/api-credentials",
        session.tenantId,
        session.token,
        {
          name: String(fields.get("name")),
          membership_id: String(fields.get("membership_id")),
          permissions: fields.getAll("permission").map(String),
          ...(fields.get("expires_at")
            ? { expires_at: String(fields.get("expires_at")) }
            : {}),
        },
        session.csrf,
      );
      setSecret({ title: "API credential — shown once", value: result.token });
      form.reset();
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Unable to create API credential",
      );
    } finally {
      setBusy(false);
    }
  }
  async function revoke(id: string) {
    if (!window.confirm("Revoke this API credential immediately?")) return;
    setBusy(true);
    try {
      await createResource(
        `/api/v1/integrations/api-credentials/${id}/revoke`,
        session.tenantId,
        session.token,
        {},
        session.csrf,
      );
      setNotice("API credential revoked.");
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Unable to revoke credential",
      );
    } finally {
      setBusy(false);
    }
  }
  async function createWebhook(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    const form = event.currentTarget,
      fields = new FormData(form),
      patterns = String(fields.get("event_patterns"))
        .split(/[\n,]+/)
        .map((item) => item.trim())
        .filter(Boolean);
    try {
      const result = await createResource<{
        id: string;
        signingSecret: string;
        version: number;
      }>(
        "/api/v1/integrations/webhooks",
        session.tenantId,
        session.token,
        {
          name: String(fields.get("name")),
          url: String(fields.get("url")),
          event_patterns: patterns,
        },
        session.csrf,
      );
      setSecret({
        title: "Webhook signing secret — shown once",
        value: result.signingSecret,
      });
      form.reset();
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Unable to create webhook",
      );
    } finally {
      setBusy(false);
    }
  }
  async function toggle(
    endpoint: IntegrationSettingsData["endpoints"][number],
  ) {
    setBusy(true);
    try {
      await createResource(
        `/api/v1/integrations/webhooks/${endpoint.id}`,
        session.tenantId,
        session.token,
        {
          name: endpoint.name,
          url: endpoint.url,
          event_patterns: endpoint.event_patterns,
          status: endpoint.status === "active" ? "disabled" : "active",
          expected_version: endpoint.version,
        },
        session.csrf,
        "PUT",
      );
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Unable to update webhook",
      );
    } finally {
      setBusy(false);
    }
  }
  if (!data)
    return (
      <section className="panel">
        <p>{error || "Loading API and webhook settings…"}</p>
        {error && (
          <button className="secondary" onClick={() => void load()}>
            Retry
          </button>
        )}
      </section>
    );
  return (
    <div className="integration-settings">
      {secret && (
        <section className="connector-token">
          <div>
            <strong>{secret.title}</strong>
            <small>Copy this value now. CHIS will not show it again.</small>
            <code>{secret.value}</code>
          </div>
          <button
            className="secondary"
            onClick={() => void navigator.clipboard.writeText(secret.value)}
          >
            <Copy />
            Copy
          </button>
        </section>
      )}
      {notice && <p className="form-success">{notice}</p>}
      {error && <p className="form-error">{error}</p>}
      <section className="panel integration-section">
        <header>
          <div>
            <KeyRound />
            <span>
              <h3>Scoped API credentials</h3>
              <p>
                Bearer credentials inherit current operator access and are
                additionally limited to selected scopes.
              </p>
            </span>
          </div>
        </header>
        <form
          className="settings-form integration-create"
          onSubmit={(event) => void createCredential(event)}
        >
          <label>
            Name
            <input
              name="name"
              minLength={2}
              maxLength={120}
              required
              placeholder="Reporting automation"
            />
          </label>
          <label>
            Operator
            <select name="membership_id" required defaultValue="">
              <option value="" disabled>
                Select operator
              </option>
              {operators
                .filter((row) => value(row, "membership_status") === "active")
                .map((row) => (
                  <option
                    key={value(row, "membership_id")}
                    value={value(row, "membership_id")}
                  >
                    {value(row, "display_name")} · {value(row, "email")}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Expires
            <input name="expires_at" type="datetime-local" />
          </label>
          <fieldset>
            <legend>Scopes</legend>
            <div className="permission-grid compact">
              {permissions.map((item) => (
                <label key={value(item, "key")}>
                  <input
                    type="checkbox"
                    name="permission"
                    value={value(item, "key")}
                  />
                  <span>
                    <strong>{value(item, "key")}</strong>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          <button className="primary" disabled={busy}>
            <Plus />
            Create credential
          </button>
        </form>
        <div className="integration-list">
          {data.credentials.map((item) => (
            <article key={item.id}>
              <div>
                <strong>{item.name}</strong>
                <small>
                  {item.token_prefix} · {item.permissions.join(", ")}
                </small>
              </div>
              <span
                className={`status ${item.status === "active" ? "success" : "neutral"}`}
              >
                <i />
                {item.status}
              </span>
              <small>
                Last used{" "}
                {item.last_used_at
                  ? new Date(item.last_used_at).toLocaleString()
                  : "never"}
              </small>
              {item.status === "active" && (
                <button
                  className="ghost danger-text"
                  disabled={busy}
                  onClick={() => void revoke(item.id)}
                >
                  Revoke
                </button>
              )}
            </article>
          ))}
        </div>
      </section>
      <section className="panel integration-section">
        <header>
          <div>
            <Webhook />
            <span>
              <h3>Signed webhooks</h3>
              <p>
                HTTPS delivery uses timestamped HMAC-SHA256 signatures, bounded
                retry and dead-letter evidence.
              </p>
            </span>
          </div>
        </header>
        <form
          className="settings-form integration-create"
          onSubmit={(event) => void createWebhook(event)}
        >
          <label>
            Name
            <input name="name" minLength={2} maxLength={120} required />
          </label>
          <label>
            HTTPS endpoint
            <input
              name="url"
              type="url"
              pattern="https://.*"
              required
              placeholder="https://hooks.example.com/chis"
            />
          </label>
          <label>
            Event patterns
            <textarea
              name="event_patterns"
              required
              placeholder="subscriber.*&#10;aaa.*"
            />
          </label>
          <button className="primary" disabled={busy}>
            <Plus />
            Add webhook
          </button>
        </form>
        <div className="integration-list">
          {data.endpoints.map((endpoint) => (
            <article key={endpoint.id}>
              <div>
                <strong>{endpoint.name}</strong>
                <small>{endpoint.url}</small>
                <small>{endpoint.event_patterns.join(" · ")}</small>
              </div>
              <span
                className={`status ${endpoint.status === "active" ? "success" : "neutral"}`}
              >
                <i />
                {endpoint.status}
              </span>
              <button
                className="ghost"
                disabled={busy}
                onClick={() => void toggle(endpoint)}
              >
                {endpoint.status === "active" ? "Disable" : "Enable"}
              </button>
            </article>
          ))}
        </div>
        <div className="panel table-panel integration-deliveries">
          <table>
            <thead>
              <tr>
                <th>Event</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>HTTP/error</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {data.deliveries.map((item) => (
                <tr key={item.id}>
                  <td>{item.event_type}</td>
                  <td>{item.status}</td>
                  <td>{item.attempts}</td>
                  <td>{item.response_status ?? item.last_error_code ?? "—"}</td>
                  <td>{new Date(item.updated_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
